'use client'

import * as React from 'react'
import { cn } from '../../lib/utils'
import { ThemaMotief, type PortalThemaSoort } from './thema-motieven'
import { THEMA_BANNER_ANIMATIE_MS, THEMA_DEELTJES } from './thema-constanten'

/**
 * batch-67 portal-thema (FO-504) - de compacte themabanner bovenaan het
 * portal-dashboard (maximaal 96 px hoog): motief links, kop en korte tekst
 * rechts, een wegklikknop van 44 px, en een subtiele animatie die
 * `prefers-reduced-motion` respecteert en na zes seconden stilvalt.
 *
 * Het component is "dom": het rekent niets uit en onthoudt niets. De portal
 * bepaalt of de banner getoond wordt (server-side thema + localStorage-
 * wegklik), het beheerscherm toont hem als voorbeeld zonder wegklikknop.
 *
 * Animatie: Tailwind-keyframes `thema-zweef` (motief, 3 x 2 s), `thema-val`
 * (vallende deeltjes, 2,8 s met een vertraging tot 3 s) en `thema-twinkel`
 * (sterretjes, 2 x 1,5 s), altijd achter de `motion-safe:`-variant zodat ze
 * bij reduced-motion niet bestaan. Vertraging plus duur blijft onder de zes
 * seconden, dus de timer die de deeltjes daarna uit de DOM haalt kapt niets
 * halverwege af. De deeltjes hebben als basis `opacity-0`: zonder animatie zijn
 * ze onzichtbaar.
 *
 * Tekst: op 375 px is er naast het motief en de knop ongeveer 197 px over.
 * De kop mag daarom over twee regels lopen (lange voornamen, de leeftijd-
 * variant, "Vanavond is het zover: op naar 2027") en de tekst eronder ook: de
 * standaardteksten van 40 tot 50 tekens passen niet op één regel en werden
 * anders midden in de zin afgekapt. Gemeten in headless Chrome: 74 px met één
 * regel kop, hooguit 91 px met twee regels kop én twee regels tekst, dus altijd
 * onder de grens van 96 px (`max-h-24`) en zonder dat er iets wordt afgesneden.
 */

export interface ThemaBannerThema {
  soort: PortalThemaSoort
  titel: string
  tekst: string | null
  /** Hoogtepunt van de periode: meer deeltjes. */
  hoogtepunt: boolean
}

export interface ThemaBannerProps {
  thema: ThemaBannerThema
  /** Wegklikken voor vandaag; zonder callback is er geen knop (voorbeeldmodus). */
  onSluiten?: () => void
  /** Animatie helemaal uit (bv. in een statische voorbeeldlijst). Default aan. */
  animatie?: boolean
  className?: string
}

type Deeltje = 'val' | 'twinkel'

const DEELTJE_SOORT: Record<PortalThemaSoort, Deeltje> = {
  VERJAARDAG: 'val', // confetti
  SINTERKLAAS: 'val', // pepernoten
  KERST: 'val', // sneeuw
  NIEUWJAAR: 'twinkel', // vuurwerk-sterretjes
  PASEN: 'twinkel',
  KONINGSDAG: 'twinkel',
}

/** Kleur per thema voor de deeltjes (STREEP-palet; oranje alleen Koningsdag). */
const DEELTJE_KLEUR: Record<PortalThemaSoort, readonly string[]> = {
  VERJAARDAG: ['#CCFA78', '#1A1A18', '#CCFA78'],
  SINTERKLAAS: ['#1A1A18', '#1A1A18', '#5C5C58'],
  // Sneeuw op een wit vlak: de bg- en border-tint, anders is hij onzichtbaar.
  KERST: ['#BABDB0', '#CED0C8', '#BABDB0'],
  NIEUWJAAR: ['#CCFA78', '#F2C94C', '#1A1A18'],
  PASEN: ['#CCFA78', '#CED0C8', '#CCFA78'],
  KONINGSDAG: ['#F97316', '#CCFA78', '#F97316'],
}

const BANNER_KLEUR: Record<PortalThemaSoort, string> = {
  VERJAARDAG: 'border-streep-border bg-streep-surface',
  SINTERKLAAS: 'border-streep-border bg-streep-surface',
  KERST: 'border-streep-border bg-streep-surface',
  NIEUWJAAR: 'border-streep-border bg-streep-surface',
  PASEN: 'border-streep-border bg-streep-surface',
  KONINGSDAG: 'border-orange-200 bg-orange-50',
}

/**
 * Leest de reduced-motion-voorkeur na de mount (SSR kent geen media query) en
 * zet een eenmalige timer die de animatie na `THEMA_BANNER_ANIMATIE_MS` stopt.
 */
function useThemaAnimatie(aan: boolean): { stil: boolean } {
  const [stil, setStil] = React.useState(!aan)
  React.useEffect(() => {
    if (!aan) return
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setStil(true)
      return
    }
    const timer = window.setTimeout(() => setStil(true), THEMA_BANNER_ANIMATIE_MS)
    return () => window.clearTimeout(timer)
  }, [aan])
  return { stil }
}

function Deeltjes({ soort, hoogtepunt }: { soort: PortalThemaSoort; hoogtepunt: boolean }) {
  const soortDeeltje = DEELTJE_SOORT[soort]
  const kleuren = DEELTJE_KLEUR[soort]
  const lijst = hoogtepunt ? THEMA_DEELTJES : THEMA_DEELTJES.slice(0, 12)
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {lijst.map(([links, vertraging, grootte], i) => (
        <span
          key={i}
          className={cn(
            'absolute top-0 block opacity-0',
            soortDeeltje === 'val'
              ? 'motion-safe:animate-thema-val'
              : 'motion-safe:animate-thema-twinkel',
            soort === 'VERJAARDAG' ? 'rounded-[1px]' : 'rounded-full',
          )}
          style={{
            left: `${links}%`,
            top: soortDeeltje === 'twinkel' ? `${(i * 37) % 80}%` : undefined,
            width: grootte,
            height: soort === 'VERJAARDAG' ? grootte * 1.6 : grootte,
            backgroundColor: kleuren[i % kleuren.length],
            animationDelay: `${vertraging}s`,
          }}
        />
      ))}
    </div>
  )
}

export function ThemaBanner({ thema, onSluiten, animatie = true, className }: ThemaBannerProps) {
  const { stil } = useThemaAnimatie(animatie)
  return (
    <section
      aria-label={thema.titel}
      data-thema={thema.soort}
      className={cn(
        'relative flex max-h-24 min-h-[4.5rem] items-center gap-3 overflow-hidden rounded-xl border px-3 py-2',
        BANNER_KLEUR[thema.soort],
        className,
      )}
    >
      <div
        className={cn(
          'relative z-10 h-14 w-14 shrink-0',
          !stil && 'motion-safe:animate-thema-zweef',
        )}
      >
        <ThemaMotief soort={thema.soort} className="h-14 w-14" />
      </div>
      <div className="relative z-10 min-w-0 flex-1">
        <p className="line-clamp-2 text-[15px] font-semibold leading-tight text-streep-donker">
          {thema.titel}
        </p>
        {thema.tekst ? (
          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-streep-text-muted">
            {thema.tekst}
          </p>
        ) : null}
      </div>
      {onSluiten ? (
        <button
          type="button"
          onClick={onSluiten}
          aria-label="Verberg dit thema voor vandaag"
          className="relative z-10 -mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-streep-text-muted hover:bg-black/5 hover:text-streep-donker focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-streep-groen"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      ) : null}
      {!stil ? <Deeltjes soort={thema.soort} hoogtepunt={thema.hoogtepunt} /> : null}
    </section>
  )
}
